/**
 * src/store/slices/onboardingSlice.js
 * Manages all 3 onboarding steps in Redux.
 *
 * State stores:
 *  - token: the invite token from URL
 *  - role: viewer/member/admin/manager (controls whether step 2 is shown)
 *  - requiresDocuments: false for viewer, true for everyone else
 *  - onboardingStep: 0/1/2/3
 *  - onboardedUser: the result from set-password — shown on Congrats page
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import onboardingService from "../../services/onboardingService";

const extractError = (error) =>
  error?.response?.data?.message || error?.message || "Something went wrong.";

// ── Thunks ──────────────────────────────────────────────────────────

// Step 0 — validate token, get user info
export const fetchOnboardingInfo = createAsyncThunk(
  "onboarding/fetchInfo",
  async (token, { rejectWithValue }) => {
    try {
      const data = await onboardingService.getOnboardingInfo(token);
      return data.data;
      // { email, role, designation, dateOfJoining, onboardingStep, requiresDocuments }
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Step 1 — submit profile details
export const submitOnboardingProfile = createAsyncThunk(
  "onboarding/submitProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      const data = await onboardingService.submitProfile(profileData);
      return data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Step 2 — submit documents (FormData)
export const submitOnboardingDocuments = createAsyncThunk(
  "onboarding/submitDocuments",
  async (formData, { rejectWithValue }) => {
    try {
      const data = await onboardingService.submitDocuments(formData);
      return data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// Step 3 — set password → returns onboarded user data for Congrats page
export const submitOnboardingPassword = createAsyncThunk(
  "onboarding/submitPassword",
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const data = await onboardingService.submitSetPassword({
        token,
        password,
      });
      return data.data;
      // { name, role, dateOfJoining, employeeId?, designation? }
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// ── Initial State ────────────────────────────────────────────────────

const initialState = {
  // Token from URL — stored so steps can share it
  token: null,

  // Info from GET /onboarding/info
  email: null,
  role: null,
  requiresDocuments: true,
  onboardingStep: 0,

  // Final result from set-password — read by Congrats page
  onboardedUser: null,

  // Loading flags per step
  isLoadingInfo: false,
  isSubmittingProfile: false,
  isSubmittingDocuments: false,
  isSubmittingPassword: false,

  // Errors per step
  infoError: null,
  profileError: null,
  documentsError: null,
  passwordError: null,

  // Success flags per step
  profileSuccess: false,
  documentsSuccess: false,
  passwordSuccess: false,
};

// ── Slice ────────────────────────────────────────────────────────────

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,

  reducers: {
    // Store token on first load (from URL param)
    setOnboardingToken: (state, action) => {
      state.token = action.payload;
    },
    // Clear errors
    clearOnboardingErrors: (state) => {
      state.infoError = null;
      state.profileError = null;
      state.documentsError = null;
      state.passwordError = null;
    },
    // Reset entire onboarding state
    resetOnboarding: () => initialState,
  },

  extraReducers: (builder) => {
    // ── fetchOnboardingInfo ───────────────────
    builder
      .addCase(fetchOnboardingInfo.pending, (state) => {
        state.isLoadingInfo = true;
        state.infoError = null;
      })
      .addCase(fetchOnboardingInfo.fulfilled, (state, action) => {
        state.isLoadingInfo = false;
        state.email = action.payload.email;
        state.role = action.payload.role;
        state.requiresDocuments = action.payload.requiresDocuments;
        state.onboardingStep = action.payload.onboardingStep;
      })
      .addCase(fetchOnboardingInfo.rejected, (state, action) => {
        state.isLoadingInfo = false;
        state.infoError = action.payload;
      });

    // ── submitOnboardingProfile ───────────────
    builder
      .addCase(submitOnboardingProfile.pending, (state) => {
        state.isSubmittingProfile = true;
        state.profileError = null;
        state.profileSuccess = false;
      })
      .addCase(submitOnboardingProfile.fulfilled, (state) => {
        state.isSubmittingProfile = false;
        state.profileSuccess = true;
        state.onboardingStep = 1;
      })
      .addCase(submitOnboardingProfile.rejected, (state, action) => {
        state.isSubmittingProfile = false;
        state.profileError = action.payload;
      });

    // ── submitOnboardingDocuments ─────────────
    builder
      .addCase(submitOnboardingDocuments.pending, (state) => {
        state.isSubmittingDocuments = true;
        state.documentsError = null;
        state.documentsSuccess = false;
      })
      .addCase(submitOnboardingDocuments.fulfilled, (state) => {
        state.isSubmittingDocuments = false;
        state.documentsSuccess = true;
        state.onboardingStep = 2;
      })
      .addCase(submitOnboardingDocuments.rejected, (state, action) => {
        state.isSubmittingDocuments = false;
        state.documentsError = action.payload;
      });

    // ── submitOnboardingPassword ──────────────
    builder
      .addCase(submitOnboardingPassword.pending, (state) => {
        state.isSubmittingPassword = true;
        state.passwordError = null;
        state.passwordSuccess = false;
      })
      .addCase(submitOnboardingPassword.fulfilled, (state, action) => {
        state.isSubmittingPassword = false;
        state.passwordSuccess = true;
        state.onboardingStep = 3;
        // Store the onboarded user for Congrats page
        state.onboardedUser = action.payload;
      })
      .addCase(submitOnboardingPassword.rejected, (state, action) => {
        state.isSubmittingPassword = false;
        state.passwordError = action.payload;
      });
  },
});

export const { setOnboardingToken, clearOnboardingErrors, resetOnboarding } =
  onboardingSlice.actions;

export default onboardingSlice.reducer;
